import { Component, OnInit, Inject, ElementRef, AfterViewInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SvcMediaCarouselComponent } from '../../svc-media-carousel/svc-media-carousel.component';
import { SvcMediaCarouselItem } from '../../svc-media-carousel/interfaces/svc-media-carousel-item';

export type SvcImagesViewerItem = {
  url: string;
  type: 'image' | 'video';
  current?: boolean;
  /**
   * AB#72797 — caption of the expanded media. `label` is the short line above (e.g. "Passo 3/4")
   * and `title` the body text. Absent on every existing caller, which keeps the viewer as it was.
   */
  label?: string;
  title?: string;
};
export type SvcImagesViewerConfig = {
  fitContent?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
};

@Component({
  selector: 'svc-images-viewer-modal',
  templateUrl: './svc-images-viewer-modal.component.html',
  styleUrls: ['./svc-images-viewer-modal.component.scss'],
  host: {
    '(document:keydown)': 'onKeydown($event)',
    '(window:resize)': 'onWindowResize()',
  },
})
export class SvcImagesViewerModalComponent implements OnInit, AfterViewInit {
  @ViewChild('mediasContainer') protected mediasContainer: ElementRef<HTMLElement>;
  @ViewChild(SvcMediaCarouselComponent) protected carousel: SvcMediaCarouselComponent;

  protected config: SvcImagesViewerConfig = {
    fitContent: 'contain',
  };
  protected carouselItems: SvcMediaCarouselItem[] = [];
  protected carouselInitialItem: SvcMediaCarouselItem;
  protected carouselHeight: number = 0;
  /** AB#72797 — caption of the item on screen; follows the carousel as the user navigates. */
  protected currentItem?: SvcMediaCarouselItem;

  private get element(): HTMLElement {
    return this.elRef?.nativeElement;
  }

  private get mediasContainerEl(): HTMLElement {
    return this.mediasContainer?.nativeElement;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      imagesUrl: string | SvcImagesViewerItem[];
      config?: SvcImagesViewerConfig;
    },
    public dialogRef: MatDialogRef<SvcImagesViewerModalComponent>,
    public elRef: ElementRef<HTMLElement>
  ) {
    const imagesUrl: SvcImagesViewerItem[] = Array.isArray(data.imagesUrl) ? data.imagesUrl : data.imagesUrl ? [{ url: data.imagesUrl, type: 'image' }] : [];
    this.carouselItems = imagesUrl.map((object) => {
      const item = {
        url: object.url,
        type: object.type,
        label: object.label,
        title: object.title,
      };
      if (object.current) {
        this.carouselInitialItem = item;
      }
      return item;
    });
    this.currentItem = this.carouselInitialItem ?? this.carouselItems[0];
    this.config = {
      ...this.config,
      ...(data.config ?? {}),
    };

    this.dialogRef.disableClose = true;
  }

  ngOnInit() {
    this.onWindowResize();
    this.addClassMatDialogContainer();
  }

  ngAfterViewInit(): void {
    this.addClassMatDialogContainer();
  }

  private addClassMatDialogContainer(): void {
    this.element?.parentElement.classList.add('bg-transparent', 'p-0');
  }

  onKeydown(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (event.keyCode === 27) this.close();
  }

  onWindowResize(): void {
    // AB#71408: previously capped at a fixed 410px regardless of viewport size, so the "expand"
    // view rendered smaller than the source step image. Reserve just enough room for the
    // thumbnails strip/close button and let the image use the rest of the viewport height.
    this.carouselHeight = Math.max(window.innerHeight - 140, 300);
  }

  protected prevMediasPreview(): void {
    if (this.mediasContainerEl) {
      this.mediasContainerEl.scrollLeft -= 68;
    }
  }

  protected nextMediasPreview(): void {
    if (this.mediasContainerEl) {
      this.mediasContainerEl.scrollLeft += 68;
    }
  }

  /** AB#72797 — keep the caption in step with the media the carousel is showing. */
  protected onCurrentChanged(item: SvcMediaCarouselItem): void {
    this.currentItem = item ?? this.currentItem;
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
